import { skipToken, useQueries } from '@tanstack/react-query';
import { useParams, useRouter, useRouterState } from '@tanstack/react-router';
import { assignedAgencies } from '../../../features/auth/agencyAccess';
import { useShigoSession } from '../../../lib/useShigoSession';
import { readShigoAppConfig } from '../../../lib/config';
import { resolveBreadcrumbs, isKnownBreadcrumbRoute } from '../../../lib/breadcrumbs';
import {
  buildCampaignHierarchyCrumbs,
  resolveCampaignHierarchyScope,
  searchBrandId,
  type BrandBreadcrumbData,
  type CampaignBreadcrumbData,
} from './campaignHierarchyCrumbs';
import type { MadBuyLineItem } from '../../../features/campaigns/campaigns/lineItemsModel';
import { brandsQueryKey } from '../../../features/campaigns/brands/hooks';
import { agenciesQueryKey } from '../../../features/supply/agency/hooks';

import type { QueryClient, QueryKey } from '@tanstack/react-query';

import { networksQueryKey } from '../../../features/admin/networks/hooks';
import { campaignsQueryKey } from '../../../features/campaigns/campaigns/hooks';
import { creativesQueryKey } from '../../../features/campaigns/creatives/hooks';
import {
  supplyResourceQueryKey,
  SupplyQueryResource,
} from '../../../features/campaigns/supply/hooks';
import { dealsQueryKey } from '../../../features/demand/deals/hooks';
import { integrationDetailsQueryKey } from '../../../features/demand/integrations/hooks';
import { ownersDetailsQueryKey } from '../../../features/demand/owners/hooks';
import { demandPartnerDetailsQueryKey } from '../../../features/demand/partners/hooks';
import { supplyAppsQueryKey } from '../../../features/supply/apps/hooks';
import { listsQueryKey } from '../../../features/supply/lists/hooks';
import { placementsQueryKey } from '../../../features/supply/placements/hooks';
import { publishersQueryKey } from '../../../features/supply/publishers/hooks';
import { targetingBuilderQueryKey } from '../../../features/targeting/builder/hooks';
import { BreadcrumbEntity } from '../../../lib/breadcrumbs';

export interface BreadcrumbQueryContext {
  params: Record<string, string | undefined>;
  pathname: string;
  brandId: string;
  publisherIds: string[];
}

export function breadcrumbQueryKey(
  entity: BreadcrumbEntity,
  context: BreadcrumbQueryContext,
): QueryKey {
  const { params, pathname, brandId, publisherIds } = context;
  const id = params[entity];
  switch (entity) {
    case BreadcrumbEntity.Publisher:
      return pathname.startsWith('/p/')
        ? ['dashboards', 'publishers', [...publisherIds].sort()]
        : [...publishersQueryKey, 'detail', id];
    case BreadcrumbEntity.Placement:
      return [...placementsQueryKey, 'detail', id];
    case BreadcrumbEntity.Partner:
      return [...demandPartnerDetailsQueryKey, id];
    case BreadcrumbEntity.Integration:
      return [...integrationDetailsQueryKey, params.demandPartnerId, id];
    case BreadcrumbEntity.Network:
      return [...networksQueryKey, 'detail', id];
    case BreadcrumbEntity.Campaign:
      return [...campaignsQueryKey, 'detail', id];
    case BreadcrumbEntity.Creative:
      return [...creativesQueryKey, 'detail', brandId, id];
    case BreadcrumbEntity.Deal:
      return [...dealsQueryKey, 'detail', id];
    case BreadcrumbEntity.Owner:
      return [...ownersDetailsQueryKey, id];
    case BreadcrumbEntity.App:
      return [...supplyAppsQueryKey, 'detail', id];
    case BreadcrumbEntity.List:
      return [...listsQueryKey, pathname.includes('/schain/') ? 'schain' : 'rewrite', 'detail', id];
    case BreadcrumbEntity.Targeting:
      return [...targetingBuilderQueryKey, 'detail', id];
    case BreadcrumbEntity.LineItem:
      return ['campaigns', 'line-item', id];
    case BreadcrumbEntity.TailoredSupply:
      return [...supplyResourceQueryKey(SupplyQueryResource.Tailored), 'detail', id];
  }
}

export function cachedBreadcrumbName(
  client: QueryClient,
  entity: BreadcrumbEntity,
  context: BreadcrumbQueryContext,
  fallback: string,
): string | null {
  const state = client.getQueryState<{ name?: string; content?: { id: string; name: string }[] }>(
    breadcrumbQueryKey(entity, context),
  );
  const data = state?.data;
  const name =
    context.pathname.startsWith('/p/') && entity === BreadcrumbEntity.Publisher
      ? data?.content?.find((item) => item.id === context.params.publisherId)?.name
      : data?.name;
  if (name?.trim()) return name;
  if (state?.status === 'error' || state?.status === 'success') return fallback;
  return null;
}

// Observe the page-owned queries only. Navigation never starts API requests.
export function useBreadcrumbs(rootTitle: string) {
  const router = useRouter();
  const location = useRouterState({ select: (state) => state.location });
  const matches = useRouterState({ select: (state) => state.matches });
  const params = useParams({ strict: false });
  const { session } = useShigoSession();
  const routeId = matches.at(-1)?.routeId;
  const routePath = routeId ? router.routesById[routeId].fullPath : '';
  const context: BreadcrumbQueryContext = {
    pathname: location.pathname,
    params,
    brandId: searchBrandId(location.search),
    publisherIds: session?.user.publisherIds ?? [],
  };
  const { route, agency } = resolveCampaignHierarchyScope(
    location.pathname,
    location.search,
    assignedAgencies(session?.user),
  );
  const { networkId } = readShigoAppConfig();
  const prefix = agency ? ['agency-demand', networkId, agency.id] : [];
  const [campaign] = useQueries({
    queries: (route?.campaignId
      ? [[...prefix, ...campaignsQueryKey, 'detail', route.campaignId]]
      : []
    ).map((queryKey) => ({ queryKey, queryFn: skipToken })),
  });
  const campaignData = campaign?.data as CampaignBreadcrumbData | undefined;
  const brandId = route?.brandId ?? campaignData?.brandId ?? campaignData?.brand?.id;
  const [brand, agencyDetails, lineItem] = useQueries({
    queries: (route
      ? [
          [...prefix, ...brandsQueryKey, 'detail', brandId ?? ''],
          [...agenciesQueryKey, networkId, 'detail', route.agencyId ?? ''],
          [...prefix, 'campaigns', 'line-item', route.lineItemId],
        ]
      : []
    ).map((queryKey) => ({ queryKey, queryFn: skipToken })),
  });
  if (!route)
    return {
      context,
      crumbs: resolveBreadcrumbs(
        location.pathname,
        routePath,
        (path) => isKnownBreadcrumbRoute(router, path),
        rootTitle,
      ),
    };
  const crumbs = buildCampaignHierarchyCrumbs({
    route,
    workspaceAgency: agency,
    brandId,
    campaign: campaign && { data: campaignData, isPending: campaign.isPending },
    brand: brand && {
      data: brand.data as BrandBreadcrumbData | undefined,
      isPending: brand.isPending,
      isSuccess: brand.isSuccess,
    },
    agencyDetails: agencyDetails && {
      data: agencyDetails.data as { id: string; name: string } | undefined,
      isPending: agencyDetails.isPending,
    },
    lineItem: lineItem && {
      data: lineItem.data as MadBuyLineItem | undefined,
      isPending: lineItem.isPending,
    },
  });
  return { context, crumbs };
}
